import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import WarrantyCardPresentation, {
  type WarrantyCardProductView,
} from "@/components/warranty/WarrantyCardPresentation";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { CLARITY_ONE_ASSISTANCE } from "@shared/clarityOneContact";
import {
  WARRANTY_CLARITY_SUPPORT_EXPLANATION,
  WARRANTY_MANUFACTURER_AUTHORITY_STATEMENT,
  WARRANTY_MANDATORY_RIGHTS_NOTE,
  WARRANTY_NON_TRANSFERABILITY_STATEMENT,
} from "@shared/warrantyDomain";
import { Loader2, Search, ShieldCheck } from "lucide-react";

type WarrantyListItem = {
  id: number;
  brand: string;
  model: string;
  productName: string;
  finish: string | null;
  serialNumber: string | null;
  serialNotApplicable: boolean;
  warrantyStartDate: Date | string | null;
  warrantyExpiryDate: Date | string | null;
  warrantyDurationMonths: number | null;
  durationLabel: string | null;
  status: string;
  manufacturerWarrantyLabel: string | null;
  manufacturerTermsUrl: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
};

const STATUS_ORDER: Record<string, number> = {
  active: 0,
  expiring_soon: 1,
  expired: 2,
  unknown: 3,
  review_required: 0,
};

const WARRANTY_TERMS = {
  claritySupportExplanation: WARRANTY_CLARITY_SUPPORT_EXPLANATION,
  nonTransferability: WARRANTY_NON_TRANSFERABILITY_STATEMENT,
  manufacturerAuthority: WARRANTY_MANUFACTURER_AUTHORITY_STATEMENT,
  mandatoryRightsNote: WARRANTY_MANDATORY_RIGHTS_NOTE,
};

function toProductView(item: WarrantyListItem): WarrantyCardProductView {
  return {
    id: item.id,
    brand: item.brand,
    model: item.model,
    productName: item.productName,
    finish: item.finish,
    serialNumber: item.serialNumber,
    serialNotApplicable: item.serialNotApplicable,
    warrantyStartDate: item.warrantyStartDate,
    warrantyExpiryDate: item.warrantyExpiryDate,
    warrantyDurationMonths: item.warrantyDurationMonths,
    manufacturerWarrantyLabel: item.manufacturerWarrantyLabel,
    manufacturerTermsUrl: item.manufacturerTermsUrl,
    imageUrl: item.imageUrl,
    imageAlt: item.imageAlt,
    durationLabel: item.durationLabel,
    coverageStatus: item.status as WarrantyCardProductView["coverageStatus"],
  };
}

export default function ClarityOneWarrantyPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [, setLocation] = useLocation();
  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const { data, isLoading, error } = trpc.clarityOneRecords.listWarrantyItems.useQuery(undefined, {
    enabled: !!me,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!meLoading && !me) setLocation(clarityOneLoginUrlWithRedirect("/warranty"));
  }, [meLoading, me, setLocation]);

  const items = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99),
      ) as WarrantyListItem[],
    [data],
  );

  const showWarrantySearch = items.length > 5;
  const filteredItems = useMemo(() => {
    const needle = searchQuery.trim().toLocaleLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      [item.productName, item.brand, item.model, item.serialNumber]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(needle)),
    );
  }, [items, searchQuery]);

  const customer = useMemo(
    () =>
      me
        ? {
            name: me.name,
            contact: me.contact,
            email: me.email,
            maskPII: false,
          }
        : null,
    [me],
  );

  if (meLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  return (
    <ClarityOneLayout showNav>
      <div data-testid="clarity-one-warranty">
        {error ? (
          <p className="mb-6 text-sm text-[#8B7355]">
            We could not load your warranty items. Please try again.
          </p>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[#B8956E]" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-[#E8E4DC] bg-white px-6 py-14 text-center">
            <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-[#B8956E]" strokeWidth={1.25} />
            <h2 className="mb-2 text-lg font-medium text-[#1A2744]">No warranty items yet</h2>
            <p className="mx-auto max-w-md text-sm leading-relaxed text-[#8B7355]">
              Issued warranty coverage will appear here once available.
            </p>
          </div>
        ) : customer ? (
          <>
            {showWarrantySearch ? (
              <div className="relative mb-5">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8B7355]"
                />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by product name or serial number"
                  aria-label="Search warranty items"
                  className="h-12 w-full rounded-xl border border-[#E8E4DC] bg-white pl-12 pr-4 text-sm text-[#1A2744] outline-none transition focus:border-[#B8956E] focus:ring-2 focus:ring-[#B8956E]/20"
                />
              </div>
            ) : null}

            {filteredItems.length > 0 ? (
              <WarrantyCardPresentation
                products={filteredItems.map(toProductView)}
                customer={customer}
                mode="issued"
                downloadAccess="customer"
                showLoginCta={false}
                allowCustomerPrivacyToggle
                terms={WARRANTY_TERMS}
                onContactSupport={() =>
                  window.open(CLARITY_ONE_ASSISTANCE.whatsAppUrl, "_blank", "noopener,noreferrer")
                }
              />
            ) : (
              <div className="rounded-2xl border border-[#E8E4DC] bg-white px-6 py-12 text-center">
                <h2 className="mb-2 text-lg font-medium text-[#1A2744]">No matching warranty items</h2>
                <p className="text-sm text-[#8B7355]">Try another product name or serial number.</p>
              </div>
            )}
          </>
        ) : null}
      </div>
    </ClarityOneLayout>
  );
}
